import React, { useState } from 'react'
import { useAudioMarkerEngine } from './useAudioMarkerEngine'
import { Marker } from './useAudioMarkerEngine'

type AudioEngine = ReturnType<typeof useAudioMarkerEngine>

interface MarkerTimelineProps {
  engine: AudioEngine
}

/**
 * MarkerTimeline - Visualization and management of markers and loop sections
 * Displays a horizontal timeline with marker positions and loop ranges
 */
export default function MarkerTimeline({ engine }: MarkerTimelineProps) {
  const [selectedMarkers, setSelectedMarkers] = useState<Set<string>>(new Set())
  const canCreateLoop =
    selectedMarkers.size === 2 && engine.markers.length >= 2

  const handleMarkerClick = (markerId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (e.ctrlKey || e.metaKey) {
      // Toggle selection with Ctrl/Cmd click
      const newSelected = new Set(selectedMarkers)
      if (newSelected.has(markerId)) {
        newSelected.delete(markerId)
      } else {
        newSelected.add(markerId)
      }
      setSelectedMarkers(newSelected)
    } else {
      // Single click: seek to marker
      engine.seek(
        engine.markers.find((m) => m.id === markerId)?.time ?? 0
      )
    }
  }

  const handleCreateLoop = () => {
    const [m1, m2] = Array.from(selectedMarkers)
    const marker1 = engine.markers.find((m) => m.id === m1)
    const marker2 = engine.markers.find((m) => m.id === m2)
    if (marker1 && marker2) {
      const [start, end] =
        marker1.time < marker2.time ? [m1, m2] : [m2, m1]
      engine.createLoop(start, end)
      setSelectedMarkers(new Set())
    }
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-hidden">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between">
        <h3 className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
          Markers ({engine.markers.length})
        </h3>
        {canCreateLoop && (
          <button
            onClick={handleCreateLoop}
            className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-colors"
          >
            Create Loop
          </button>
        )}
      </div>

      {/* Timeline visualization */}
      <div className="shrink-0 space-y-3">
        {/* Markers timeline */}
        {engine.markers.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-neutral-500">
              Markers (Cmd/Ctrl+Click to select, Click to seek)
            </div>
            <div className="relative h-12 bg-neutral-900 rounded border border-neutral-800 p-2 overflow-x-auto">
              <div className="relative h-full">
                {/* Duration baseline */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  {/* Ticks every 10% */}
                  {[...Array(11)].map((_, i) => (
                    <g key={`tick-${i}`}>
                      <line
                        x1={`${i * 10}%`}
                        y1="0"
                        x2={`${i * 10}%`}
                        y2="100%"
                        stroke="rgba(255,255,255,0.05)"
                        strokeWidth="1"
                      />
                      {i % 2 === 0 && (
                        <text
                          x={`${i * 10}%`}
                          y="14"
                          fontSize="9"
                          fill="rgba(255,255,255,0.3)"
                          textAnchor="middle"
                          pointerEvents="none"
                        >
                          {formatTime(
                            (i * 10 * engine.duration) / 100
                          )}
                        </text>
                      )}
                    </g>
                  ))}
                </svg>

                {/* Markers */}
                {engine.markers.map((marker) => {
                  const percent = (marker.time / engine.duration) * 100
                  const isSelected = selectedMarkers.has(marker.id)
                  return (
                    <div
                      key={marker.id}
                      className={`
                        absolute top-1/2 -translate-y-1/2 cursor-pointer
                        transition-all
                        ${
                          isSelected
                            ? 'bg-blue-500 w-1 h-8'
                            : 'bg-purple-500/60 hover:bg-purple-500 w-0.5 h-6'
                        }
                      `}
                      style={{
                        left: `${percent}%`,
                        transform: 'translateX(-50%) translateY(-50%)',
                      }}
                      onClick={(e) => handleMarkerClick(marker.id, e)}
                      title={`${marker.label || 'Marker'} @ ${formatTime(marker.time)}`}
                      role="button"
                      tabIndex={0}
                    />
                  )
                })}

                {/* Current position line */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-white/50 pointer-events-none z-10"
                  style={{
                    left: `${
                      (engine.currentTime / engine.duration) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Loop sections */}
        {engine.loopSections.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-neutral-500">
              Loop Sections ({engine.loopSections.length})
            </div>
            <div className="space-y-2">
              {engine.loopSections.map((loop) => {
                const startMarker = engine.markers.find(
                  (m) => m.id === loop.startMarkerId
                )
                const endMarker = engine.markers.find(
                  (m) => m.id === loop.endMarkerId
                )
                if (!startMarker || !endMarker) return null

                return (
                  <div
                    key={loop.id}
                    className={`
                      flex items-center justify-between gap-2 px-3 py-2 rounded border
                      transition-colors
                      ${
                        engine.activeLoopSection?.id === loop.id
                          ? 'bg-emerald-900/40 border-emerald-700/60 text-emerald-200'
                          : 'bg-neutral-800/30 border-neutral-700/40 text-neutral-400 hover:text-neutral-300'
                      }
                    `}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button
                        onClick={() => {
                          if (engine.activeLoopSection?.id === loop.id) {
                            engine.clearLoop()
                          } else {
                            // Activate loop (would need to add this to hook)
                            // For now, just show visual feedback
                          }
                        }}
                        className="text-xs font-medium"
                      >
                        {formatTime(startMarker.time)} → {formatTime(endMarker.time)}
                      </button>
                      <span className="text-xs text-neutral-600">
                        ({formatTime(endMarker.time - startMarker.time)})
                      </span>
                    </div>
                    <button
                      onClick={() => engine.deleteLoop(loop.id)}
                      className="text-xs px-2 py-1 rounded bg-neutral-700/40 hover:bg-rose-900/40 hover:text-rose-300 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* No markers state */}
        {engine.markers.length === 0 && (
          <div className="text-xs text-neutral-600 text-center py-4">
            No markers yet. Press <kbd className="px-1 py-0.5 bg-neutral-800 rounded text-neutral-300 font-mono text-xs">Space</kbd> to create one.
          </div>
        )}
      </div>

      {/* Marker list */}
      {engine.markers.length > 0 && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-1">
            {engine.markers.map((marker) => (
              <div
                key={marker.id}
                className={`
                  flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs
                  transition-colors cursor-pointer
                  ${
                    selectedMarkers.has(marker.id)
                      ? 'bg-blue-900/40 border border-blue-700/60 text-blue-200'
                      : 'bg-neutral-800/20 border border-neutral-700/20 hover:bg-neutral-800/40'
                  }
                `}
                onClick={(e) => handleMarkerClick(marker.id, e)}
              >
                <span className="font-mono text-neutral-400">
                  {formatTime(marker.time)}
                </span>
                <span className="text-neutral-500 truncate flex-1">
                  {marker.label || '(Untitled)'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    engine.deleteMarker(marker.id)
                  }}
                  className="text-neutral-600 hover:text-rose-400 transition-colors"
                  aria-label="Delete marker"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 100)
  return `${mins}:${String(secs).padStart(2, '0')}.${String(ms).padStart(2, '0')}`
}
